import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api";
import { AuthContext } from "../AuthContext";
import AccountDetail from "./AccountDetail";
import AccountsList from "./AccountsList";
import AddAccountModal from "./AddAccountModal";
import StatementDetail from "./StatementDetail";
import DueSoon from "./DueSoon";
import ProfilesRail, { ProfileLogo } from "./ProfilesRail";
import ProfileModal from "./ProfileModal";
import SettingsModal from "./SettingsModal";
import SupplierOverview from "./SupplierOverview";
import { SettingsContext, DEFAULT_SETTINGS } from "../SettingsContext";

const STORAGE_KEYS = {
  profile: "accountz.selectedProfileId",
  account: "accountz.selectedAccountId",
  statement: "accountz.openStatement",
};

const readSavedStatement = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEYS.statement) || "null");
  } catch {
    return null;
  }
};

const Dashboard = () => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [openStatement, setOpenStatement] = useState(null);
  const [extractingFileIds, setExtractingFileIds] = useState(() => new Set());
  const [extractingStatementIds, setExtractingStatementIds] = useState(() => new Set());
  const [statementsRefreshKey, setStatementsRefreshKey] = useState(0);
  const [profilesRailCollapsed, setProfilesRailCollapsed] = useState(false);
  const [accountTypes, setAccountTypes] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [showSettings, setShowSettings] = useState(false);
  const [showOverview, setShowOverview] = useState(false);
  const [settingsPrefill, setSettingsPrefill] = useState(null);
  const [logoUrls, setLogoUrls] = useState({});
  const logoUrlsRef = useRef({});
  const pendingAccountRef = useRef(null);
  const savedStatementRef = useRef(readSavedStatement());
  const restoredRef = useRef(false);
  const { user, logout } = React.useContext(AuthContext);

  useEffect(() => {
    // Restore last-open panels (profile / account / statement) after sign-in or reload
    const savedProfileId = localStorage.getItem(STORAGE_KEYS.profile);
    const savedAccountId = localStorage.getItem(STORAGE_KEYS.account);
    if (savedAccountId) pendingAccountRef.current = savedAccountId;
    fetchProfiles(savedProfileId || undefined);
    fetchAccountTypes();
    fetchAllAccounts();
    fetchAllInvoices();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  const openLabelMapping = (pattern) => {
    setSettingsPrefill(pattern || "");
    setShowSettings(true);
  };

  const addSupplier = async (name, tag) => {
    const trimmed = (name || "").trim();
    if (!trimmed || !tag) return;
    const exists = (settings.suppliers || []).some(
      (s) => s.tag === tag && s.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) return;
    try {
      const res = await axios.put(`${API}/settings`, {
        suppliers: [
          ...(settings.suppliers || []),
          { id: window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()), name: trimmed, tag },
        ],
      });
      setSettings(res.data);
      toast.success(`"${trimmed}" saved as a supplier under ${tag}`);
    } catch (error) {
      console.error("Failed to save supplier:", error);
      toast.error("Failed to save supplier");
    }
  };

  const addEmployee = async (name, tag) => {
    const trimmed = (name || "").trim();
    if (!trimmed || !tag) return;
    const exists = (settings.employees || []).some(
      (e) => e.tag === tag && e.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) return;
    try {
      const res = await axios.put(`${API}/settings`, {
        employees: [
          ...(settings.employees || []),
          { id: window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()), name: trimmed, tag },
        ],
      });
      setSettings(res.data);
      toast.success(`"${trimmed}" saved as an employee under ${tag}`);
    } catch (error) {
      console.error("Failed to save employee:", error);
      toast.error("Failed to save employee");
    }
  };

  // Reload accounts whenever the selected profile changes
  useEffect(() => {
    if (selectedProfile) {
      fetchAccounts();
    }
  }, [selectedProfile?.id]);

  // Close any open statement when switching accounts (or restore a saved one on first mount)
  useEffect(() => {
    if (!selectedAccount) {
      if (restoredRef.current) setOpenStatement(null);
      return;
    }
    const saved = savedStatementRef.current;
    if (saved && saved.accountId === selectedAccount.id) {
      savedStatementRef.current = null;
      (async () => {
        try {
          const res = await axios.post(`${API}/accounts/${selectedAccount.id}/statements`, {
            year: saved.year,
            month: saved.month,
          });
          setOpenStatement(res.data);
        } catch (error) {
          console.error("Failed to restore statement:", error);
          setOpenStatement(null);
        } finally {
          restoredRef.current = true;
        }
      })();
      return;
    }
    savedStatementRef.current = null;
    restoredRef.current = true;
    setOpenStatement(null);
  }, [selectedAccount?.id]);

  // Persist selections so the same panels re-open after a session timeout / reload
  useEffect(() => {
    if (!restoredRef.current) return;
    if (selectedProfile?.id) localStorage.setItem(STORAGE_KEYS.profile, selectedProfile.id);
  }, [selectedProfile?.id]);

  useEffect(() => {
    if (!restoredRef.current) return;
    if (selectedAccount?.id) localStorage.setItem(STORAGE_KEYS.account, selectedAccount.id);
  }, [selectedAccount?.id]);

  useEffect(() => {
    if (!restoredRef.current) return;
    if (openStatement) {
      localStorage.setItem(
        STORAGE_KEYS.statement,
        JSON.stringify({
          accountId: openStatement.account_id,
          year: openStatement.year,
          month: openStatement.month,
        })
      );
    } else {
      localStorage.removeItem(STORAGE_KEYS.statement);
    }
  }, [openStatement?.id]);

  const fetchProfiles = async (preferredId) => {
    try {
      const response = await axios.get(`${API}/profiles`);
      setProfiles(response.data);

      setSelectedProfile((prev) => {
        const target =
          (preferredId && response.data.find((p) => p.id === preferredId)) ||
          (prev && response.data.find((p) => p.id === prev.id)) ||
          response.data[0] ||
          null;
        return target;
      });
    } catch (error) {
      console.error("Failed to fetch profiles:", error);
    }
  };

  // All accounts across every profile (powers the aggregated "Due Soon" banner)
  const fetchAllAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAllAccounts(response.data);
    } catch (error) {
      console.error("Failed to fetch all accounts:", error);
    }
  };

  // Unpaid invoices across every account (powers Due Soon for invoice-mode accounts)
  const fetchAllInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices?unpaid=true`);
      setAllInvoices(response.data);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    }
  };

  const fetchAccounts = async () => {
    if (!selectedProfile) return;
    try {
      const response = await axios.get(`${API}/accounts?profile_id=${selectedProfile.id}`);
      setAccounts(response.data);

      if (pendingAccountRef.current) {
        const pending = response.data.find((acc) => acc.id === pendingAccountRef.current);
        pendingAccountRef.current = null;
        setSelectedAccount(pending || response.data[0] || null);
      } else {
        // Always keep an account selected (first one) so details are always shown
        setSelectedAccount((prev) => {
          if (prev) {
            const still = response.data.find((acc) => acc.id === prev.id);
            if (still) return still;
          }
          return response.data[0] || null;
        });
      }

      const accountsWithLogos = response.data.filter((account) => account.logo_filename);
      for (const account of accountsWithLogos) {
        fetchAccountLogo(account.id);
      }
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    }
  };

  const refreshAll = async () => {
    await fetchAccounts();
    await fetchAllAccounts();
    await fetchAllInvoices();
  };

  // Runs the AI transaction extraction from Dashboard scope so it survives
  // navigation away from StatementDetail. Multiple can run in parallel; each
  // adds/removes itself from the extracting sets, and every success bumps
  // statementsRefreshKey so the StatementsGrid (if visible) refetches.
  // Pass `{ silent: true }` to suppress per-statement toasts (used by Extract All).
  const runExtraction = async (statement, file, options = {}) => {
    const { silent = false } = options;
    setExtractingFileIds((prev) => new Set(prev).add(file.id));
    setExtractingStatementIds((prev) => new Set(prev).add(statement.id));
    try {
      const res = await axios.post(`${API}/statements/${statement.id}/extract`, { file_id: file.id });
      const count = (res.data?.transactions || []).length;
      if (!silent) toast.success(`Extracted ${count} transaction${count === 1 ? "" : "s"}`);
      setStatementsRefreshKey((k) => k + 1);
      return res.data;
    } catch (error) {
      console.error("Extraction failed:", error);
      if (!silent) toast.error(error.response?.data?.detail || "Extraction failed. Please try again.");
      return null;
    } finally {
      setExtractingFileIds((prev) => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
      setExtractingStatementIds((prev) => {
        const next = new Set(prev);
        next.delete(statement.id);
        return next;
      });
    }
  };

  // Open an account from the Due Soon banner, switching profiles if needed
  const handleDueSoonSelect = (account) => {
    if (account.profile_id === selectedProfile?.id) {
      setSelectedAccount(account);
    } else {
      pendingAccountRef.current = account.id;
      const prof = profiles.find((p) => p.id === account.profile_id);
      if (prof) setSelectedProfile(prof);
    }
  };

  const fetchAccountLogo = async (accountId) => {
    try {
      const response = await axios.get(`${API}/accounts/${accountId}/logo`, {
        responseType: "blob",
      });
      const logoUrl = URL.createObjectURL(response.data);
      setLogoUrls((prev) => {
        const old = prev[accountId];
        if (old && old.startsWith("blob:")) {
          URL.revokeObjectURL(old);
        }
        const next = { ...prev, [accountId]: logoUrl };
        logoUrlsRef.current = next;
        return next;
      });
    } catch (error) {
      console.error(`Failed to fetch logo for account ${accountId}:`, error);
    }
  };

  const fetchAccountTypes = async () => {
    try {
      const response = await axios.get(`${API}/account-types`);
      setAccountTypes(response.data.default_types);
    } catch (error) {
      console.error("Failed to fetch account types:", error);
    }
  };

  // Revoke all blob URLs only when the dashboard unmounts (not on every logoUrls change).
  useEffect(() => {
    return () => {
      Object.values(logoUrlsRef.current).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const handleAddProfile = () => {
    setEditingProfile(null);
    setShowProfileModal(true);
  };

  const handleEditProfile = (p) => {
    setEditingProfile(p);
    setShowProfileModal(true);
  };

  const handleProfileSaved = async (profileId) => {
    await fetchProfiles(profileId);
  };

  const filteredAccounts = accounts.filter((a) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesQuery =
      !q ||
      [a.name, a.company_provider, a.account_number, a.account_type].some(
        (v) => v && v.toLowerCase().includes(q)
      );
    const matchesStatus = statusFilter === "All" || a.status === statusFilter;
    return matchesQuery && matchesStatus;
  });

  return (
    <SettingsContext.Provider value={{ settings, openLabelMapping, addSupplier, addEmployee }}>
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="flex flex-col space-y-1 mr-3">
                <div className="w-6 h-1.5 bg-slate-700 rounded-sm"></div>
                <div className="w-6 h-1.5 bg-slate-700 rounded-sm"></div>
                <div className="w-6 h-1.5 bg-teal-400 rounded-sm"></div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Accountz</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600" data-testid="welcome-message">
                Welcome, {user?.full_name}
              </span>
              <button
                onClick={() => setShowOverview(true)}
                data-testid="global-overview-button"
                title="Spending overview by tag & supplier"
                className="text-gray-500 hover:text-gray-800 p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
              </button>
              <button
                onClick={() => setShowSettings(true)}
                data-testid="settings-button"
                title="Settings"
                className="text-gray-500 hover:text-gray-800 p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1800px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1 flex flex-col min-h-0 overflow-hidden">
        <DueSoon accounts={allAccounts} invoices={allInvoices} profiles={profiles} onSelect={handleDueSoonSelect} />

        <div className="flex gap-6 flex-1 min-h-0">
          <ProfilesRail
            profiles={profiles}
            selectedProfileId={selectedProfile?.id}
            onSelect={setSelectedProfile}
            onAddProfile={handleAddProfile}
            onEditProfile={handleEditProfile}
            collapsed={profilesRailCollapsed}
            onToggleCollapsed={() => setProfilesRailCollapsed((v) => !v)}
          />

          {/* Combined Accounts panel: list + always-on details */}
          <div className="flex-1 min-h-0">
            <div className="bg-[#f3f4f6] rounded-lg shadow flex overflow-hidden h-full">
              {/* Account list (left sub-column) */}
              <div className="w-72 flex-shrink-0 border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200 bg-[#e1e2e4] flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2.5 min-w-0">
                    {selectedProfile && (
                      <ProfileLogo profile={selectedProfile} className="w-9 h-9" />
                    )}
                    <div className="min-w-0">
                      <h2 className="text-base font-semibold text-gray-900 truncate" data-testid="accounts-profile-name">
                        {selectedProfile ? selectedProfile.name : "Accounts"}
                      </h2>
                      {selectedProfile && selectedProfile.description && (
                        <p className="text-xs text-gray-700 mt-0.5 line-clamp-2" data-testid="accounts-profile-description">
                          {selectedProfile.description}
                        </p>
                      )}
                      {selectedProfile && (
                        <p className="text-xs text-gray-500 mt-0.5" data-testid="accounts-profile-stats">
                          {selectedProfile.profile_type} · {accounts.length}{" "}
                          {accounts.length === 1 ? "account" : "accounts"} ·{" "}
                          {accounts.filter((a) => a.status === "Active").length} active
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAddAccount(true)}
                    data-testid="add-account-button"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded-md text-xs font-medium flex-shrink-0"
                  >
                    + Add
                  </button>
                </div>
                {accounts.length > 0 && (
                  <div className="px-3 pt-3 flex gap-2">
                    <input
                      type="text"
                      placeholder="Search accounts..."
                      data-testid="account-search-input"
                      className="flex-1 min-w-0 border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <select
                      data-testid="account-status-filter"
                      className="border border-gray-300 rounded-md px-1.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white flex-shrink-0"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="All">All</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </div>
                )}
                <div className="p-3 overflow-y-auto flex-1">
                  {accounts.length === 0 ? (
                    <div className="text-center py-8" data-testid="empty-accounts">
                      <p className="text-gray-500 text-sm">No accounts yet. Add your first one!</p>
                    </div>
                  ) : filteredAccounts.length === 0 ? (
                    <div className="text-center py-8" data-testid="no-matching-accounts">
                      <p className="text-gray-500 text-sm">No accounts match your search.</p>
                    </div>
                  ) : (
                    <AccountsList
                      accounts={filteredAccounts}
                      selectedAccount={selectedAccount}
                      onSelect={setSelectedAccount}
                      logoUrls={logoUrls}
                    />
                  )}
                </div>
              </div>

              {/* Account details (right sub-column, always shown, no close button) */}
              <div className="flex-1 overflow-y-auto bg-white">
                {selectedAccount ? (
                  openStatement ? (
                    <StatementDetail
                      account={selectedAccount}
                      statement={openStatement}
                      onBack={() => setOpenStatement(null)}
                      onUpdate={refreshAll}
                      onExtract={runExtraction}
                      extractingFileIds={extractingFileIds}
                    />
                  ) : (
                    <AccountDetail
                      account={selectedAccount}
                      onUpdate={refreshAll}
                      logoUrl={logoUrls[selectedAccount.id]}
                      onLogoUpdate={(accountId) => fetchAccountLogo(accountId)}
                      onFilesChange={refreshAll}
                      onOpenStatement={setOpenStatement}
                      statementsRefreshKey={statementsRefreshKey}
                      extractingStatementIds={extractingStatementIds}
                      onExtract={runExtraction}
                      onStatementsViewModeChange={(mode) => {
                        if (mode === "table") setProfilesRailCollapsed(true);
                      }}
                    />
                  )
                ) : (
                  <div className="p-12 text-center" data-testid="dashboard-welcome">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No account selected</h3>
                    <p className="text-gray-600 mb-6">
                      Add an account to this profile to view and manage its details and documents.
                    </p>
                    <button
                      onClick={() => setShowAddAccount(true)}
                      data-testid="create-account-button"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium"
                    >
                      Create an Account
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Account Modal */}
      {showAddAccount && selectedProfile && (
        <AddAccountModal
          accountTypes={accountTypes}
          profileId={selectedProfile.id}
          onClose={() => setShowAddAccount(false)}
          onAdd={async () => {
            await refreshAll();
            await fetchProfiles(selectedProfile.id);
          }}
        />
      )}

      {/* Profile Modal (create / edit) */}
      {showProfileModal && (
        <ProfileModal
          profile={editingProfile}
          onClose={() => setShowProfileModal(false)}
          onSaved={handleProfileSaved}
        />
      )}

      {/* Global Spending Overview */}
      {showOverview && <SupplierOverview onClose={() => setShowOverview(false)} />}

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal
          settings={settings}
          prefillPattern={settingsPrefill}
          onClose={() => {
            setShowSettings(false);
            setSettingsPrefill(null);
          }}
          onSaved={async () => {
            await fetchSettings();
            setStatementsRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
    </SettingsContext.Provider>
  );
};

export default Dashboard;
